import { ApiError } from '@artic/api-client';

/**
 * Boundary helper for Orval-generated request functions.
 *
 * The generated request functions return a discriminated `{ data, status }`
 * union whose error branches (400 / 401 / 403 / 404 in the OpenAPI spec) type
 * `data` as `void`, while the success `data` is the generated DTO (which can
 * differ structurally from the quality-side type — e.g. `applied_cat_score:
 * number` vs the `1|2|3|4|5` literal union). The fetch mutator throws `ApiError`
 * on a non-2xx before the data is read, so a successful response always carries
 * the body.
 *
 * `expectData` re-asserts that success shape — the contract the previous
 * `customFetch<{ data: T }>` calls (and every downstream consumer) relied on —
 * and throws if the body is missing, so an empty-body regression surfaces as an
 * error instead of silently caching `undefined`.
 */
export function expectData<T>(res: { data: unknown }): T {
  if (res.data == null) {
    throw new Error('Expected a response body but the server returned none.');
  }
  return res.data as T;
}

/**
 * Human-readable message for a failed request, for toasts.
 *
 * `ApiError.message` alone is often just the backend exception's summary —
 * nestjs-zod 400s all say exactly "Validation failed" while the diagnosis
 * (which field, why) sits in the body's `errors: ZodIssue[]`. This unwraps
 * up to three of those issues into "field: reason" fragments so the toast
 * says e.g. "Validation failed — work_order_ids: Array must contain at most
 * 500 element(s)" instead of leaving the user (and us) guessing.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const message = err.message || fallback;
    const errors = err.bodyAsRecord?.['errors'];
    const details = (Array.isArray(errors) ? errors : [])
      .map((issue: unknown) => {
        if (!issue || typeof issue !== 'object') return null;
        const record = issue as Record<string, unknown>;
        const reason = typeof record['message'] === 'string' ? record['message'] : null;
        if (!reason) return null;
        const path = Array.isArray(record['path'])
          ? record['path']
              .filter((p): p is string | number => typeof p === 'string' || typeof p === 'number')
              .join('.')
          : '';
        return path ? `${path}: ${reason}` : reason;
      })
      .filter((d): d is string => d !== null)
      .slice(0, 3);
    return details.length > 0 ? `${message} — ${details.join('; ')}` : message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
